import React from 'react';
import { Case } from '../../services/api';
import { Plus } from 'lucide-react';
import './CasesList.css';

interface CasesListProps {
  cases: Case[];
  casesLoading: boolean;
  casesError: string | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  currentPage: number;
  hasMore: boolean;
  highestPage: number;
  onCaseClick: (caseId: string) => void;
  onNewCase: () => void;
  onLoadCases: (page: number) => void;
  formatDate: (date: string) => string;
}

const CasesList: React.FC<CasesListProps> = ({
  cases,
  casesLoading,
  casesError,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  currentPage,
  hasMore,
  highestPage,
  onCaseClick,
  onNewCase,
  onLoadCases,
  formatDate,
}) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'status-open';
      case 'archived':
        return 'status-archived';
      case 'closed':
        return 'status-closed';
      default:
        return 'status-default';
    }
  };

  const filteredCases = cases.filter((caseItem) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      caseItem.projectName.toLowerCase().includes(searchLower) ||
      caseItem.clientName?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <>
      <div className="cases-controls">
        <div className="cases-controls-left">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search cases by name, client, or number..."
            className="search-input"
          />
          <button className="new-case-btn" onClick={onNewCase}>
            <Plus size={20} />
            New Case
          </button>
        </div>

        <div className="status-filters">
          <button
            className={`filter-btn ${statusFilter === '' ? 'active' : ''}`}
            onClick={() => setStatusFilter('')}
          >
            All
          </button>
          <button
            className={`filter-btn ${statusFilter === 'Open' ? 'active' : ''}`}
            onClick={() => setStatusFilter('Open')}
          >
            Open
          </button>
          <button
            className={`filter-btn ${statusFilter === 'Archived' ? 'active' : ''}`}
            onClick={() => setStatusFilter('Archived')}
          >
            Archived
          </button>
        </div>
      </div>

      {casesLoading && (
        <div className="cases-loading">
          <div className="loading-spinner"></div>
          <p>Loading cases...</p>
        </div>
      )}

      {casesError && (
        <div className="cases-error">
          <p>❌ {casesError}</p>
          <button onClick={() => onLoadCases(currentPage)} className="retry-button">
            Retry
          </button>
        </div>
      )}

      {!casesLoading && !casesError && filteredCases.length === 0 && (
        <div className="cases-empty">
          <p>No cases found</p>
        </div>
      )}

      {!casesLoading && !casesError && filteredCases.length > 0 && (
        <div className="cases-table-container">
          <table className="cases-table">
            <thead>
              <tr>
                <th>Project Name</th>
                <th>Client</th>
                <th>Status</th>
                <th>Phase</th>
                <th>Created</th>
                <th>Last Activity</th>
                <th>Notes</th>
                <th>Docs</th>
              </tr>
            </thead>
            <tbody>
              {filteredCases.map((caseItem) => (
                <tr
                  key={caseItem.caseId}
                  onClick={() => onCaseClick(caseItem.caseId)}
                  className="case-row"
                >
                  <td className="project-name">{caseItem.projectName}</td>
                  <td>{caseItem.clientName}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(caseItem.status)}`}>
                      {caseItem.status}
                    </span>
                  </td>
                  <td>{caseItem.phase || '-'}</td>
                  <td>{formatDate(caseItem.createDate || caseItem.createdAt || '')}</td>
                  <td>{formatDate(caseItem.lastActivity || caseItem.lastModified || '')}</td>
                  <td className="stat-cell">{caseItem.noteCount}</td>
                  <td className="stat-cell">{caseItem.docCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="cases-pagination">
            <div className="cases-count">
              Showing {filteredCases.length} {filteredCases.length === 1 ? 'case' : 'cases'} (Page {currentPage})
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => onLoadCases(1)}
                disabled={currentPage === 1 || casesLoading}
                title="First Page"
              >
                «
              </button>
              <button
                className="pagination-btn"
                onClick={() => onLoadCases(currentPage - 1)}
                disabled={currentPage === 1 || casesLoading}
                title="Previous Page"
              >
                ‹
              </button>
              <input
                type="number"
                className="page-input"
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page > 0) {
                    onLoadCases(page);
                  }
                }}
                min="1"
                disabled={casesLoading}
              />
              <button
                className="pagination-btn"
                onClick={() => onLoadCases(currentPage + 1)}
                disabled={!hasMore || casesLoading}
                title="Next Page"
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => onLoadCases(highestPage)}
                disabled={currentPage === highestPage || casesLoading}
                title="Last Page (Highest Loaded)"
              >
                »
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CasesList;

